import { getNewDefaultBoard, mainBoardLayout } from "./communicationBoardDefaults";
import type {
  CommunicationBoardData,
  CategoryCell,
  UtteranceCell,
} from "./communicationBoardTypes";

type Lang = "he" | "en" | "ru" | "ar";

// Gender-aware name mapping for green verb categories (Hebrew only)
const GREEN_CATEGORY_GENDER_NAMES: Record<string, { male: string; female: string }> = {};

// Build the mapping from mainBoardLayout
for (const row of mainBoardLayout) {
  for (const item of row) {
    if (item && item.type === "category" && item.color === "green" && item.nameFemale?.he) {
      GREEN_CATEGORY_GENDER_NAMES[item.id] = {
        male: item.name.he,
        female: item.nameFemale.he,
      };
    }
  }
}

// Russian inner item text replacements for cat-want (v45 migration)
const RU_WANT_REPLACEMENTS: Record<string, string> = {
  "туалет": "в туалет",
  "свет": "включить свет",
  "темноту": "выключить свет",
};

// Russian gender-aware inner items
const RU_GENDER_REPLACEMENTS: Record<string, { male: string; female: string }> = {
  "побыть одному": { male: "побыть одному", female: "побыть одной" },
  "устал": { male: "устал", female: "устала" },
  "я устал": { male: "я устал", female: "я устала" },
  "я голоден": { male: "я голоден", female: "я голодна" },
  "очень устал": { male: "очень устал", female: "очень устала" },
};

function migrateRussianInnerItems(
  boards: CommunicationBoardData[],
  language: Lang,
  gender: "male" | "female" | "neutral"
): { boards: CommunicationBoardData[]; changed: boolean } {
  if (language !== "ru") return { boards, changed: false };
  const isFemale = gender === "female";
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || (cell.id !== "cat-want" && cell.id !== "cat-feel" && cell.id !== "cat-phrases" && cell.id !== "cat-sleep" && cell.id !== "cat-meeting" && cell.id !== "cat-meal")) return cell;
        const newUtterances = cell.utterances.map((u) => {
          let newText = u.text;
          // Apply text replacements
          if (RU_WANT_REPLACEMENTS[newText]) {
            newText = RU_WANT_REPLACEMENTS[newText];
          }
          // Apply gender replacements
          const genderEntry = RU_GENDER_REPLACEMENTS[u.text] || RU_GENDER_REPLACEMENTS[newText];
          if (genderEntry) {
            newText = isFemale ? genderEntry.female : genderEntry.male;
          }
          if (newText !== u.text) {
            boardChanged = true;
            return { ...u, text: newText };
          }
          return u;
        });
        return { ...cell, utterances: newUtterances };
      })
    );
    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

function migrateGenderNames(
  boards: CommunicationBoardData[],
  language: Lang,
  gender: "male" | "female" | "neutral"
): { boards: CommunicationBoardData[]; changed: boolean } {
  // Gender names only apply to Hebrew (category names)
  if (language !== "he") return { boards, changed: false };

  const isFemale = gender === "female";
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category") return cell;
        const genderNames = GREEN_CATEGORY_GENDER_NAMES[cell.id];
        if (!genderNames) return cell;

        const expectedName = isFemale ? genderNames.female : genderNames.male;
        const oppositeName = isFemale ? genderNames.male : genderNames.female;

        // Only update if the name matches the opposite gender (avoid overwriting custom names)
        if (cell.name === oppositeName) {
          boardChanged = true;
          return { ...cell, name: expectedName };
        }
        return cell;
      })
    );

    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

function arraysEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function categoryTexts(category: CategoryCell): string[] {
  return category.utterances.map((u) => u.text);
}

function buildDefaultCategoryTextsById(language: Lang): Map<string, string[]> {
  const defaultBoard = getNewDefaultBoard(language);
  const byId = new Map<string, string[]>();

  for (const row of defaultBoard.cells) {
    for (const cell of row) {
      if (cell?.type === "category") {
        byId.set(cell.id, cell.utterances.map((u) => u.text));
      }
    }
  }

  return byId;
}

// Legacy defaults (before recent updates). We only auto-migrate when the stored
// category EXACTLY matches these legacy lists, to avoid overwriting caregiver edits.
const LEGACY_YELLOW_CATEGORIES: Record<string, Record<Lang, string[]>> = {
  "cat-sleep": {
    he: [
      "עייף",
      "רוצה לישון",
      "לא יכול לישון",
      "התעוררתי",
      "חלום",
      "סיוט",
      "צריך שמיכה",
      "קר לי",
      "חם לי",
      "רוצה אור",
    ],
    en: [
      "tired",
      "want to sleep",
      "can't sleep",
      "woke up",
      "dream",
      "nightmare",
      "need blanket",
      "I'm cold",
      "I'm hot",
      "want light",
    ],
    ru: [
      "устал",
      "хочу спать",
      "не могу спать",
      "проснулся",
      "сон",
      "кошмар",
      "нужно одеяло",
      "мне холодно",
      "мне жарко",
      "хочу свет",
    ],
    ar: [
      "متعب",
      "أريد أن أنام",
      "لا أستطيع النوم",
      "استيقظت",
      "حلم",
      "كابوس",
      "أحتاج بطانية",
      "أشعر بالبرد",
      "أشعر بالحر",
      "أريد ضوء",
    ],
  },
  "cat-meal": {
    he: [
      "ארוחת בוקר",
      "ארוחת צהריים",
      "ארוחת ערב",
      "חטיף",
      "מים",
      "תה",
      "קפה",
      "מיץ",
      "פירות",
      "ירקות",
    ],
    en: [
      "breakfast",
      "lunch",
      "dinner",
      "snack",
      "water",
      "tea",
      "coffee",
      "juice",
      "fruit",
      "vegetables",
    ],
    ru: [
      "завтрак",
      "обед",
      "ужин",
      "перекус",
      "вода",
      "чай",
      "кофе",
      "сок",
      "фрукты",
      "овощи",
    ],
    ar: [
      "فطور",
      "غداء",
      "عشاء",
      "وجبة خفيفة",
      "ماء",
      "شاي",
      "قهوة",
      "عصير",
      "فواكه",
      "خضروات",
    ],
  },
  "cat-outside": {
    he: [
      "לא",
      "כן",
      "בבקשה",
      "תודה",
      "יש כאן יותר מדי רעש",
      "חם לי",
      "קר לי",
      "נעים לי",
      "לאן הולכים\u200F?",
      "מתי חוזרים\u200F?",
      "מה זה\u200F?",
      "רוצה לעצור כאן",
      "רוצה ללכת למקום אחר",
      "זה מפריע לי",
      "לאט יותר",
      "מהר יותר",
      "אפשר לחזור\u200F?",
      "לא נוח לי",
    ],
    en: [
      "no",
      "yes",
      "please",
      "thank you",
      "too much noise here",
      "I'm hot",
      "I'm cold",
      "I'm comfortable",
      "where are we going?",
      "when are we coming back?",
      "what is this?",
      "want to stop here",
      "want to go somewhere else",
      "this bothers me",
      "slower",
      "faster",
      "can we go back?",
      "I'm not comfortable",
    ],
    ru: [
      "нет",
      "да",
      "пожалуйста",
      "спасибо",
      "здесь слишком шумно",
      "мне жарко",
      "мне холодно",
      "мне хорошо",
      "куда идём?",
      "когда вернёмся?",
      "что это?",
      "хочу остановиться здесь",
      "хочу в другое место",
      "это мне мешает",
      "медленнее",
      "быстрее",
      "можно вернуться?",
      "мне неудобно",
    ],
    ar: [
      "لا",
      "نعم",
      "من فضلك",
      "شكراً",
      "ضجيج كثير هنا",
      "حار لي",
      "بارد لي",
      "مرتاح",
      "إلى أين نذهب؟",
      "متى نعود؟",
      "ما هذا؟",
      "أريد التوقف هنا",
      "أريد الذهاب لمكان آخر",
      "هذا يزعجني",
      "أبطأ",
      "أسرع",
      "هل يمكننا العودة؟",
      "لست مرتاحاً",
    ],
  },
  "cat-topics": {
    he: [
      "משפחה",
      "בריאות",
      "כסף",
      "עבודה",
      "חדשות",
      "מזג אוויר",
      "טלוויזיה",
      "ספורט",
      "זמן",
      "תוכניות",
    ],
    en: [
      "family",
      "health",
      "money",
      "work",
      "news",
      "weather",
      "TV",
      "sports",
      "time",
      "plans",
    ],
    ru: [
      "семья",
      "здоровье",
      "деньги",
      "работа",
      "новости",
      "погода",
      "телевизор",
      "спорт",
      "время",
      "планы",
    ],
    ar: [
      "العائلة",
      "الصحة",
      "المال",
      "العمل",
      "الأخبار",
      "الطقس",
      "التلفزيون",
      "الرياضة",
      "الوقت",
      "الخطط",
    ],
  },
};

function replaceCategoryUtterances(
  category: CategoryCell,
  texts: string[]
): CategoryCell {
  const newUtterances: UtteranceCell[] = texts.map((text, i) => ({
    id: `${category.id}-item-${i}`,
    type: "utterance",
    text,
    color: category.color,
  }));

  return {
    ...category,
    utterances: newUtterances,
  };
}

// Emoji migrations: old emoji -> new emoji for specific positions
// Format: { categoryId: { itemIndex: { oldEmoji: newEmoji } } }
const EMOJI_MIGRATIONS: Record<string, Record<number, { from: string; to: string }>> = {
  "cat-feel": {
    14: { from: "🌟", to: "🌈" }, // אופטימיות: star -> rainbow
  },
};

function migrateEmojis(
  boards: CommunicationBoardData[]
): { boards: CommunicationBoardData[]; changed: boolean } {
  let changed = false;

  const migrated = boards.map((board) => {
    let boardChanged = false;

    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category") return cell;

        const emojiMigrations = EMOJI_MIGRATIONS[cell.id];
        if (!emojiMigrations) return cell;

        let categoryChanged = false;
        const newUtterances = cell.utterances.map((utterance, index) => {
          const migration = emojiMigrations[index];
          if (migration && utterance.emoji === migration.from) {
            categoryChanged = true;
            return { ...utterance, emoji: migration.to };
          }
          return utterance;
        });

        if (!categoryChanged) return cell;
        boardChanged = true;
        return { ...cell, utterances: newUtterances };
      })
    );

    if (!boardChanged) return board;
    changed = true;
    return {
      ...board,
      cells: newCells,
      updatedAt: new Date().toISOString(),
    };
  });

  return { boards: migrated, changed };
}

/**
 * Replace cat-topics with cat-phrases (migration: topics → phrases)
 */
function migrateTopicsToPhrases(
  boards: CommunicationBoardData[],
  language: Lang,
  gender: "male" | "female" | "neutral" = "neutral"
): { boards: CommunicationBoardData[]; changed: boolean } {
  let changed = false;
  const defaultBoard = getNewDefaultBoard(language, gender);

  // Find cat-phrases in default board
  let defaultPhraseCell: CategoryCell | null = null;
  for (const row of defaultBoard.cells) {
    for (const cell of row) {
      if (cell?.type === "category" && cell.id === "cat-phrases") {
        defaultPhraseCell = cell;
        break;
      }
    }
    if (defaultPhraseCell) break;
  }

  if (!defaultPhraseCell) return { boards, changed };

  const migrated = boards.map((board) => {
    let boardChanged = false;
    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category" || cell.id !== "cat-topics") return cell;
        boardChanged = true;
        return defaultPhraseCell!;
      })
    );

    if (!boardChanged) return board;
    changed = true;
    return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
  });

  return { boards: migrated, changed };
}

export function migrateCommunicationBoards(
  boards: CommunicationBoardData[],
  language: Lang,
  gender: "male" | "female" | "neutral" = "neutral"
): { boards: CommunicationBoardData[]; changed: boolean } {
  const defaultsById = buildDefaultCategoryTextsById(language);

  let changed = false;
  let currentBoards = boards;

  // First pass: migrate yellow categories (text content)
  const yellowResult = currentBoards.map((board) => {
    let boardChanged = false;

    const newCells = board.cells.map((row) =>
      row.map((cell) => {
        if (!cell || cell.type !== "category") return cell;

        const legacy = LEGACY_YELLOW_CATEGORIES[cell.id]?.[language];
        const nextTexts = defaultsById.get(cell.id);
        if (!legacy || !nextTexts) return cell;

        const currentTexts = categoryTexts(cell);
        if (!arraysEqual(currentTexts, legacy)) return cell;

        boardChanged = true;
        return replaceCategoryUtterances(cell, nextTexts);
      })
    );

    if (!boardChanged) return board;
    changed = true;
    return {
      ...board,
      cells: newCells,
      updatedAt: new Date().toISOString(),
    };
  });

  currentBoards = yellowResult;

  // Second pass: migrate emojis
  const emojiResult = migrateEmojis(currentBoards);
  if (emojiResult.changed) {
    changed = true;
    currentBoards = emojiResult.boards;
  }

  // Third pass: replace cat-topics with cat-phrases
  const topicsResult = migrateTopicsToPhrases(currentBoards, language, gender);
  if (topicsResult.changed) {
    changed = true;
    currentBoards = topicsResult.boards;
  }

  // Fourth pass: fix gender-aware verb category names
  const genderResult = migrateGenderNames(currentBoards, language, gender);
  if (genderResult.changed) {
    changed = true;
    currentBoards = genderResult.boards;
  }

  // Fifth pass: fix Russian inner items and apply Russian gender
  const ruResult = migrateRussianInnerItems(currentBoards, language, gender);
  if (ruResult.changed) {
    changed = true;
    currentBoards = ruResult.boards;
  }

  return { boards: currentBoards, changed };
}
